import moment from 'moment'
import { getStatusName, getDefaultStatusName } from '@/utils/rcm.account-view'
import { getAllowedEventsByRole } from '@/utils/rcm.role-permissions'
import vueI18n from '@/plugins/vue-i18n'

export default {
  accounts: state => {
    return state.accounts.map((account, index) => {
      return {
        uniqueIndex: index,
        id: account.id,
        invoiceNumber: getDefaultStatusName(account.invoiceNumber),
        healthcarePlanCoverage: getDefaultStatusName(account.healthcarePlanCoverage),
        originEventId: account.originEventId,
        createdAt: moment(account.createdAt).format('DD/MM/YYYY HH:mm'),
        payerName: account.payerName,
        invoiceAmount: account.invoiceAmount,
        requiredSupportDocumentsCompleted: account.requiredSupportDocumentsCompleted,
        agreementCode: account.agreementCode,
				agreementName: account.agreementName,
        status: getStatusName(account.status),
        serviceTypes: account.serviceTypes,
        multiplePatientAccount: account.multiplePatientAccount,
        multipleEventSamePatientAccount: account.multipleEventSamePatientAccount,
        regime: account.regime,
        patientId: account.patientId,
        originEventCreationDateTime: account.originEventCreationDateTime ? moment(account.originEventCreationDateTime).format('DD/MM/YYYY HH:mm') : null,
        responsibleEmailAddress: account.responsibleEmailAddress,
        responsibleName: account.responsibleName,
        reassignedAccount: account.reassignedAccount,
        ripCuvFilesCompleted: account.ripCuvFilesCompleted,
        costCenterName: account.costCenterName,
        costCenterCode: account.costCenterCode,
        settlementGroupId: getDefaultStatusName(account.settlementGroupId)
      }
    })
  },
  loadingAccounts: state => state.loadingAccounts,
  accountPage: state => {
    const { page } = state
    return { ...page, number: page.number + 1 }
  },
  accountsSearchValue: state => state.accountsSearchValue,
  filtersConfigurations: state => state.filtersConfiguration,
  isRolePermissionsLoading: state => state.rolePermissions.loading,
  rolePermissionsStatuses: state => state.rolePermissions.statusesPermissions,
  rolePermissionsAccountEventRestrictions: state => state.rolePermissions.accountEventRestrictions,
  rolePermissionsSupportFileRestrictions: state => state.rolePermissions.supportFileRestrictions,
  selectedDates: state => state.selectedDates,
  selectedAccount: state => state.accountDetail.selectedAccount,
  accountCharges: state => {
    const accountChargeList = state.accountDetail.accountCharges.charges

    const charges = accountChargeList.map(charge => ({
      ...charge,
      creationDateTime: moment.parseZone(charge.creationDateTime).format('DD/MM/YYYY HH:mm'),
      originEventCreationDateTime: charge.originEventCreationDateTime
        ? moment.parseZone(charge.originEventCreationDateTime).format('DD/MM/YYYY HH:mm')
        : null
    }));    

    const { page } = state.accountDetail.accountCharges

    return {
      charges,
      page: {
        ...page,
        number: page.number + 1
      }
    }
  },
  localChargesFilters: state => state.accountDetail.accountCharges.filters || [],
  accountChargesLoading: state => state.accountDetail.accountCharges.loading,
  dateFromFilter: state => state.dateFromFilter,
  dateToFilter: state => state.dateToFilter,
  totalPendingSupportFiles: state => {
    const pendingFiles = state.accountDetail.supportFiles.all.pending
    return pendingFiles.page ? pendingFiles.page.totalElements : 0
  },
  pendingSupportFiles: state => {
    return state.accountDetail.supportFiles.all.pending
  },
  receivedSupportFiles: state => {
    return state.accountDetail.supportFiles.all.received
  },
  accountPendingSupportFiles: state => {
    return state.accountDetail.supportFiles.byAccount.pending
  },
  accountReceivedSupportFiles: state => {
    return state.accountDetail.supportFiles.byAccount.received
  },
  chargePendingSupportFiles: state => {
    return state.accountDetail.supportFiles.byCharge.pending
  },
  chargeReceivedSupportFiles: state => {
    return state.accountDetail.supportFiles.byCharge.received
  },
  supportFilesLoading: state => state.accountDetail.supportFiles.loading,
  selectedCharge: state => state.chargeDetail.selectedCharge,
  auditComments: state => state.auditComments,
  allowedEvents: (state, getters, rootState, rootGetters) => {
    const roles = rootGetters.tokenExtensions.rcmUserRoles
    const restrictedEvents = state.rolePermissions.accountEventRestrictions

    return getAllowedEventsByRole(roles, restrictedEvents)
  },
  isAllowedEvent: (state, getters) => event => {
    return getters.allowedEvents.includes(event)
  },
  agreements: state => {
    return state.agreements.content.map(agreement => {
      const copyServices = [...agreement.services]

      return {
        id: agreement.id,
        agreementCode: agreement.agreementCode,
				agreementName: agreement.agreementName ?? agreement.payerName,
        payerName: agreement.payerName,
        normative2275BuildPackageConfiguration: agreement.normative2275BuildPackageConfiguration,
        buildPackageConfigurationId: agreement.buildPackageConfigurationId,
        services: copyServices
          .sort((a, b) => a.serviceTypeFallbackName.localeCompare(b.serviceTypeFallbackName))
          .map(service => service.serviceTypeFallbackName)
      }
    })
  },
  isAgreementsLoading: state => state.agreements.loading,
  agreementsPage: state => {
    const { page } = state.agreements
    return { ...page, number: page.number + 1 }
  },
  selectedAgreement: state => state.selectedAgreement,
  agreementConfiguration: state => state.agreementConfiguration,
  selectedAuditRule: state => state.selectedAuditRule,
  healthcarePlans: state => state.healthcarePlans,
  supportFileTypes: state => state.supportFileTypes,
  serviceSupportDocumentsLoading: state => state.serviceSupportDocuments.loading,
  serviceSupportDocumentsFiles: state =>
    state.serviceSupportDocuments.byAccount.files.map(file => ({
      ...file,
      supportType: file.type ? file.type.name : 'N/A',
      supportLevel: file.supportLevel
        ? vueI18n.t('supportsAdministrator.relations.' + file.supportLevel.toLowerCase())
        : 'N/A'
    })),
  serviceSupportDocumentsPage: state => state.serviceSupportDocuments.byAccount.page,
  productSupportDocumentsFiles: state =>
    state.serviceSupportDocuments.byCharge.files.map(file => ({
      ...file,
      productCode: file.product ? file.product.code : 'N/A',
      productName: file.product ? file.product.name : 'N/A',
      supportType: file.type ? file.type.name : 'N/A'
    })),
  productSupportDocumentsPage: state => state.serviceSupportDocuments.byCharge.page,
  isAgreementProductsLoading: state => state.agreementProducts.loading,
  agreementProducts: state =>
    state.agreementProducts.content.map(product => ({
      id: product.id,
      code: product.product.code,
      name: product.product.name,
      description: product.product.description,
    })),
  agreementProductsPage: state => state.agreementProducts.page,
  displayCommentDialog: (state) => state.displayCommentDialog,
  commentControl: (state) => state.commentControl,
  payers: (state) => state.payers,
  isConcurrentAccount: (state) => state.accountDetail.isConcurrentAccount,
  isInvoicedAccount: (state) => state.accountDetail.isInvoicedAccount,
  accountReassignedAccounts: (state) => {
    return state.accountDetail.reassignedAccounts.accounts.map((account, index) => {
      return {
        uniqueIndex: index,
        id: account.id,
        invoiceNumber: getDefaultStatusName(account.invoiceNumber),
        healthcarePlanCoverage: getDefaultStatusName(account.healthcarePlanCoverage),
        originEventId: account.originEventId,
        createdAt: moment(account.createdAt).format('DD/MM/YYYY HH:mm'),
        payerName: account.payerName,
        invoiceAmount: account.invoiceAmount,
        requiredSupportDocumentsCompleted: account.requiredSupportDocumentsCompleted,
        agreementCode: account.agreementCode,
				agreementName: account.agreementName,
        status: getStatusName(account.status),
        serviceTypes: account.serviceTypes,
        multiplePatientAccount: account.multiplePatientAccount,
        multipleEventSamePatientAccount: account.multipleEventSamePatientAccount,
        regime: account.regime,
        patientId: account.patientId,
        originEventCreationDateTime: account.originEventCreationDateTime ? moment(account.originEventCreationDateTime).format('DD/MM/YYYY HH:mm') : null,
        responsibleEmailAddress: account.responsibleEmailAddress,
        responsibleName: account.responsibleName,
        reassignedAccount: account.reassignedAccount
      }
    })
  },
  loadingReassignedAccounts: state => state.accountDetail.reassignedAccounts.loadingAccounts,
  reassignedAccountsPage: state => {
    const { page } = state.accountDetail.reassignedAccounts
    return { ...page, number: page.number + 1 }
  },
  isMultiPatientAccount: (state) => state.accountDetail.isMultiPatientAccount,
  isMultiEventSamePatientAccount: (state) => state.accountDetail.isMultiEventSamePatientAccount,
  roleRcmUser: (state) => state.roleRcmUser,
  isReadyToAudit: (state) => state.accountDetail.isReadyToAudit,
  providerServiceTypes: (state) => state.providerServiceTypes.map(serviceType => ({
    text: serviceType.name,
    value: serviceType.name
  })),
  providerExecutorServices: (state) => state.providerExecutorServices.map(executorService => ({
    text: executorService.name,
    value: executorService.name
  })),
  providerAgreementPlanTypes: (state) => state.providerAgreementPlanTypes.map(agreementPlan => ({
    text: agreementPlan.name,
    value: agreementPlan.name
  })),
  providerInvoiceSources: (state) => state.providerInvoiceSources.map(invoiceSource => ({
    text: invoiceSource.name,
    value: invoiceSource.name
  })),
	providerAgreements: (state) => state.providerAgreements.map(agreement =>({
		text: agreement.agreementName ?? agreement.agreementCode,
		value: agreement.agreementCode
	})),
	providerAgreementsView: (state) => state.providerAgreements.map(agreement =>({
		name: agreement.agreementName ?? agreement.agreementCode,
		slug: agreement.agreementCode
	})),
  providerSupportDocuments: (state) => state.providerSupportDocuments.map(providerSupportFile => ({
    text: providerSupportFile.name,
    value: providerSupportFile.fileCode
  })),

  payersAvailable: (state) => state.payers.map(payer => ({
    text: payer.name,
    value: payer.name,
    slug: payer.slug
  })),
  accountListSort: (state) => state.accountListSort,
  getAgreementsFilters: (state) => state.agreementsFilters,
  preSettlementsFilters: (state) => state.preSettlements.filters,
  preSettlementsPage: (state) => {
    return {
      ...state.preSettlements.page,
      number: state.preSettlements.page.number + 1
    }
  },
  preSettlements: (state) => {
    return state.preSettlements.content.map(preSettlement => ({
      id: preSettlement.id,
      originInvoiceGroupId: preSettlement.originInvoiceGroupId,
      path: preSettlement.path,
      status: preSettlement.status,
      settlementNumber: preSettlement.settlementNumber,
      statusText: vueI18n.t('preSettlements.statuses.' + preSettlement.status),
    }))
  },
  isUploadedAgreementMatrixLoading: (state) => state.uploadedAgreementMatrix.loading,
  uploadedAgreementMatrix: (state) => {
    return state.uploadedAgreementMatrix.content.map(item => ({
      fileName: item.originalFileName,
      status: item.status,
      createdAt: moment(item.createdAt.replace(/\[.*\]$/,'')).format('DD/MM/YYYY HH:mm'),
      updatedAt: item.updatedAt ? moment(item.updatedAt.replace(/\[.*\]$/,'')).format('DD/MM/YYYY HH:mm') : null,
      id: item.id,
    }))
  },
  preSettlementDetail: (state) => state.preSettlementDetail,
  supportDocumentFiles: (state) => state.supportDocuments.documents,
  supportFormatFileFields: (state) => state.supportFormatFileFields,
  selectedPackageGroup: (state) => state.selectedPackageGroup,
  openPackageGroupDialog: (state) => state.openPackageGroupDialog,
  selectedPackageAction: (state) => state.selectedPackageAction,
  providerProducts: (state) => state.providerProducts,
  providerProductsSearchValue: (state) => state.providerProductsSearchValue,
  isProviderProductsLoading: state => state.providerProductsLoading,
  providerProductConfigurationDialog: (state) => state.providerProductConfigurationDialog,
  selectedProviderProduct: (state) => state.selectedProviderProduct,
  providerProductConfigurationIndex: (state) => state.providerProductConfigurationIndex,
  accountDefaultFiltersValuesApplied: (state) => state.accountDefaultFiltersValuesApplied,
  preSettlementFolderConfiguration: (state) => state.preSettlementFolderConfiguration,
  users: (state) => state.users.map(user => ({
    text: user.name + ' (' + user.emailAddress + ')',
    value: user.emailAddress
  })),
  providerProductsPage: state => {
    const { providerProducts } = state

    if (!providerProducts.page) return {
      size: 15,
      total_elements: 0,
      total_pages: 0
    }

    return { ...providerProducts.page }
  },
}
